import { valibotResolver } from "@hookform/resolvers/valibot";
import { type UseFormReturn, useForm } from "react-hook-form";

import { TEMPLATE_TYPES } from "common/constants";
import useTemplates from "common/datahooks/useTemplates";
import type { CreatedTemplate, Template, TemplateType } from "common/types";

import useTemplateMutations from "../../../pages/templates/datahooks/useTemplateMutations";

import type { JSX } from "react";

type CreateOrEditTemplateProps = {
  children: (
    onSubmit: () => Promise<void>,
    isLoading: boolean,
    form: UseFormReturn<Template>,
  ) => JSX.Element;
} & (
  | { initialData: CreatedTemplate; onSuccess: () => void }
  | {
      initialData: TemplateType;
      onSuccess: (createdTemplate: CreatedTemplate) => void;
    }
);

export default function CreateOrEditTemplate({
  initialData,
  onSuccess,
  children,
}: CreateOrEditTemplateProps) {
  const isNewTemplate = typeof initialData === "string";

  const { validationSchema } =
    TEMPLATE_TYPES[isNewTemplate ? initialData : initialData.type];

  const {
    createTemplate,
    updateTemplate,
    isUpdatingTemplate,
    isCreatingTemplate,
  } = useTemplateMutations();

  const { allTemplates } = useTemplates();
  const form = useForm<Template | CreatedTemplate>({
    defaultValues: isNewTemplate
      ? { type: initialData, message: "", ai_descriptors: {} }
      : initialData,
    resolver: valibotResolver(validationSchema),
  });
  const { handleSubmit, setError } = form;

  async function onSubmit(data: Template) {
    if (
      (isNewTemplate || data.name !== initialData.name) &&
      allTemplates.find(({ name }) => name === data.name.trim())
    ) {
      setError("name", { message: "Template name already exists" });
    } else if (isNewTemplate) {
      const createdTemplate = await createTemplate({
        template: {
          type: initialData,
          ...data,
        },
      });
      onSuccess(createdTemplate);
    } else {
      await updateTemplate({
        templateId: initialData.id,
        updates: data,
      });
      onSuccess();
    }
  }

  return children(
    handleSubmit(onSubmit),
    isCreatingTemplate || isUpdatingTemplate,
    form,
  );
}
